import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Search,
  Filter,
  RefreshCw,
  Eye,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Clock,
  User,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Monitor,
  Globe,
  Calendar,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Action type configs
const ACTION_CONFIGS = {
  login: { label: 'Login', color: 'bg-emerald-500/10 text-emerald-400', icon: User },
  logout: { label: 'Logout', color: 'bg-slate-500/10 text-slate-400', icon: User },
  '2fa_enabled': { label: '2FA Enabled', color: 'bg-amber-500/10 text-amber-400', icon: ShieldCheck },
  '2fa_verified': { label: '2FA Verified', color: 'bg-emerald-500/10 text-emerald-400', icon: ShieldCheck },
  '2fa_failed': { label: '2FA Failed', color: 'bg-red-500/10 text-red-400', icon: ShieldAlert },
  intake_create: { label: 'Create Intake', color: 'bg-emerald-500/10 text-emerald-400', icon: Activity },
  intake_view: { label: 'View Intake', color: 'bg-slate-500/10 text-slate-400', icon: Eye },
  intake_edit: { label: 'Edit Intake', color: 'bg-amber-500/10 text-amber-400', icon: Activity },
  intake_duplicate: { label: 'Duplicate Intake', color: 'bg-teal-500/10 text-teal-400', icon: Activity },
  intake_approve: { label: 'Approve Intake', color: 'bg-emerald-500/10 text-emerald-400', icon: CheckCircle },
  intake_promote: { label: 'Promote to Live', color: 'bg-amber-500/10 text-amber-400', icon: Shield },
  intake_archive: { label: 'Archive Intake', color: 'bg-slate-500/10 text-slate-400', icon: Activity },
  intake_delete: { label: 'Delete Intake', color: 'bg-red-500/10 text-red-400', icon: XCircle },
  prompt_edit: { label: 'Edit Prompt', color: 'bg-amber-500/10 text-amber-400', icon: Activity },
  prompt_activate: { label: 'Activate Prompt', color: 'bg-emerald-500/10 text-emerald-400', icon: Activity },
  permission_grant: { label: 'Grant Permission', color: 'bg-amber-500/10 text-amber-400', icon: Shield },
  permission_revoke: { label: 'Revoke Permission', color: 'bg-red-500/10 text-red-400', icon: ShieldAlert },
  role_change: { label: 'Change Role', color: 'bg-amber-500/10 text-amber-400', icon: Shield },
};

// Module configs
const MODULE_OPTIONS = [
  { value: '', label: 'All Modules' },
  { value: 'auth', label: 'Authentication' },
  { value: 'training_intake', label: 'Training Intake' },
  { value: 'knowledge_base', label: 'Knowledge Base' },
  { value: 'prompts', label: 'Prompts' },
  { value: 'methods', label: 'Methods' },
  { value: 'templates', label: 'Templates' },
  { value: 'permissions', label: 'Permissions' },
  { value: 'system', label: 'System' },
];

// Format UTC timestamp for display
const formatTimestamp = (utcTimestamp) => {
  if (!utcTimestamp) return '-';
  const date = new Date(utcTimestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short'
  });
};

// Log Entry Card
const AuditLogEntry = ({ log, onViewDetails }) => {
  const actionConfig = ACTION_CONFIGS[log.action] || { 
    label: log.action_label || log.action, 
    color: 'bg-slate-500/10 text-slate-400', 
    icon: Activity 
  };
  const ActionIcon = actionConfig.icon;

  return (
    <div 
      className="bg-stone-900 border border-stone-800 rounded-lg p-4 hover:border-slate-700 transition-all cursor-pointer"
      onClick={() => onViewDetails(log)}
      data-testid={`audit-log-${log.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Action info */}
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
            log.requires_2fa ? 'bg-amber-500/20' : 'bg-stone-800'
          }`}>
            <ActionIcon size={18} className={log.requires_2fa ? 'text-amber-400' : 'text-slate-400'} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`px-2 py-0.5 text-xs rounded-full ${actionConfig.color}`}>
                {actionConfig.label}
              </span>
              {log.requires_2fa && (
                <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded-full flex items-center gap-1">
                  <Shield size={10} />
                  2FA
                </span>
              )}
              {log.two_fa_verified && (
                <span className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded-full flex items-center gap-1">
                  <ShieldCheck size={10} />
                  Verified
                </span>
              )}
              {!log.success && (
                <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full">
                  Failed
                </span>
              )}
            </div>
            
            <p className="text-sm text-white font-medium truncate">
              {log.admin_email || log.admin_id}
              {log.target_label && (
                <span className="text-slate-400 font-normal"> on "{log.target_label}"</span>
              )}
            </p>
            
            {/* Changes summary */}
            {log.changes_summary && log.changes_summary.length > 0 && (
              <div className="mt-1 text-xs text-slate-500">
                {log.changes_summary.slice(0, 2).map((change, i) => (
                  <span key={i} className="mr-2">
                    {change.field}: <span className="text-red-400 line-through">{String(change.old)}</span>
                    {' → '}
                    <span className="text-emerald-400">{String(change.new)}</span>
                  </span>
                ))}
                {log.changes_summary.length > 2 && (
                  <span className="text-slate-600">+{log.changes_summary.length - 2} more</span>
                )}
              </div>
            )}
            
            {/* Reason */}
            {log.reason && (
              <p className="mt-1 text-xs text-slate-500 italic truncate">
                Reason: {log.reason}
              </p>
            )}
          </div>
        </div>
        
        {/* Right: Timestamp and context */}
        <div className="text-right shrink-0">
          <div className="text-xs font-mono text-amber-400 mb-1">
            {formatTimestamp(log.created_at_utc)}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 justify-end">
            {log.ip_address && (
              <span className="flex items-center gap-1">
                <Globe size={10} />
                {log.ip_address}
              </span>
            )}
            {log.module && (
              <span className="px-1.5 py-0.5 bg-stone-800 rounded text-slate-400">
                {log.module}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Detail Modal
const AuditDetailModal = ({ log, open, onClose }) => {
  if (!log) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-stone-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Activity size={18} className="text-amber-400" />
            Audit Log Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 text-sm">
          {/* Timestamp Section */}
          <div className="bg-stone-800/50 rounded-lg p-4">
            <h4 className="text-slate-400 text-xs uppercase tracking-wide mb-2">Timestamps (UTC)</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-slate-500">Created:</span>
                <p className="text-amber-400 font-mono">{log.created_at_utc}</p>
              </div>
              {log.action_started_at_utc && (
                <div>
                  <span className="text-slate-500">Started:</span>
                  <p className="text-slate-300 font-mono">{log.action_started_at_utc}</p>
                </div>
              )}
              {log.action_completed_at_utc && (
                <div>
                  <span className="text-slate-500">Completed:</span>
                  <p className="text-slate-300 font-mono">{log.action_completed_at_utc}</p>
                </div>
              )}
              {log.two_fa_verified_at_utc && (
                <div>
                  <span className="text-slate-500">2FA Verified:</span>
                  <p className="text-emerald-400 font-mono">{log.two_fa_verified_at_utc}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Actor Section */}
          <div className="bg-stone-800/50 rounded-lg p-4">
            <h4 className="text-slate-400 text-xs uppercase tracking-wide mb-2">Actor</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-slate-500">Email:</span>
                <p className="text-white">{log.admin_email || '-'}</p>
              </div>
              <div>
                <span className="text-slate-500">Role:</span>
                <p className="text-white capitalize">{log.admin_role || '-'}</p>
              </div>
              <div>
                <span className="text-slate-500">User ID:</span>
                <p className="text-slate-400 font-mono text-xs">{log.admin_id}</p>
              </div>
              {log.session_id && (
                <div>
                  <span className="text-slate-500">Session:</span>
                  <p className="text-slate-400 font-mono text-xs truncate">{log.session_id}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Action Section */}
          <div className="bg-stone-800/50 rounded-lg p-4">
            <h4 className="text-slate-400 text-xs uppercase tracking-wide mb-2">Action</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-slate-500">Action:</span>
                <p className="text-white">{log.action_label || log.action}</p>
              </div>
              <div>
                <span className="text-slate-500">Module:</span>
                <p className="text-white">{log.module || '-'}</p>
              </div>
              <div>
                <span className="text-slate-500">Target Type:</span>
                <p className="text-white">{log.target_type || '-'}</p>
              </div>
              <div>
                <span className="text-slate-500">Target ID:</span>
                <p className="text-slate-400 font-mono text-xs">{log.target_id || '-'}</p>
              </div>
              {log.target_label && (
                <div className="col-span-2">
                  <span className="text-slate-500">Target Label:</span>
                  <p className="text-white">{log.target_label}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Security Section */}
          <div className="bg-stone-800/50 rounded-lg p-4">
            <h4 className="text-slate-400 text-xs uppercase tracking-wide mb-2">Security Context</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-slate-500">IP Address:</span>
                <p className="text-white font-mono">{log.ip_address || '-'}</p>
              </div>
              <div>
                <span className="text-slate-500">Status:</span>
                <p className={log.success ? 'text-emerald-400' : 'text-red-400'}>
                  {log.success ? 'Success' : 'Failed'}
                </p>
              </div>
              <div>
                <span className="text-slate-500">2FA Required:</span>
                <p className={log.requires_2fa ? 'text-amber-400' : 'text-slate-400'}>
                  {log.requires_2fa ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <span className="text-slate-500">2FA Verified:</span>
                <p className={log.two_fa_verified ? 'text-emerald-400' : 'text-slate-400'}>
                  {log.two_fa_verified ? 'Yes' : 'No'}
                </p>
              </div>
              {log.user_agent && (
                <div className="col-span-2">
                  <span className="text-slate-500">User Agent:</span>
                  <p className="text-slate-400 text-xs truncate">{log.user_agent}</p>
                </div>
              )}
              {log.device_info && (
                <div className="col-span-2">
                  <span className="text-slate-500">Device:</span>
                  <p className="text-slate-400 text-xs">
                    {log.device_info.browser} / {log.device_info.os} / {log.device_info.device}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Changes Section */}
          {log.changes_summary && log.changes_summary.length > 0 && (
            <div className="bg-stone-800/50 rounded-lg p-4">
              <h4 className="text-slate-400 text-xs uppercase tracking-wide mb-2">Changes</h4>
              <div className="space-y-2">
                {log.changes_summary.map((change, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-slate-400 w-24">{change.field}:</span>
                    <span className="text-red-400 line-through bg-red-500/10 px-1 rounded">
                      {JSON.stringify(change.old)}
                    </span>
                    <ChevronRight size={12} className="text-slate-600" />
                    <span className="text-emerald-400 bg-emerald-500/10 px-1 rounded">
                      {JSON.stringify(change.new)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Before/After States */}
          {(log.before_state || log.after_state) && (
            <div className="bg-stone-800/50 rounded-lg p-4">
              <h4 className="text-slate-400 text-xs uppercase tracking-wide mb-2">State Snapshots</h4>
              <div className="grid grid-cols-2 gap-4">
                {log.before_state && (
                  <div>
                    <span className="text-red-400 text-xs">Before:</span>
                    <pre className="mt-1 text-xs text-slate-400 bg-stone-900 p-2 rounded overflow-auto max-h-32">
                      {JSON.stringify(log.before_state, null, 2)}
                    </pre>
                  </div>
                )}
                {log.after_state && (
                  <div>
                    <span className="text-emerald-400 text-xs">After:</span>
                    <pre className="mt-1 text-xs text-slate-400 bg-stone-900 p-2 rounded overflow-auto max-h-32">
                      {JSON.stringify(log.after_state, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Reason */}
          {log.reason && (
            <div className="bg-stone-800/50 rounded-lg p-4">
              <h4 className="text-slate-400 text-xs uppercase tracking-wide mb-2">Reason/Comment</h4>
              <p className="text-white">{log.reason}</p>
              {log.reason_required && (
                <span className="text-xs text-amber-400 mt-1">(Required for this action)</span>
              )}
            </div>
          )}
          
          {/* Error */}
          {log.error_message && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <h4 className="text-red-400 text-xs uppercase tracking-wide mb-2">Error</h4>
              <p className="text-red-300">{log.error_message}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Page Component
const AuditLogPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const limit = 50;
  
  // Filters
  const [filters, setFilters] = useState({
    admin_email: '',
    action: '',
    module: '',
    date_from: '',
    date_to: '',
    requires_2fa: '',
    success: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Detail modal
  const [selectedLog, setSelectedLog] = useState(null);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fundelite_token')}`,
    'Content-Type': 'application/json',
  });

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append('skip', skip.toString());
      params.append('limit', limit.toString());
      
      if (filters.admin_email) params.append('admin_email', filters.admin_email);
      if (filters.action) params.append('action', filters.action);
      if (filters.module) params.append('module', filters.module);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.requires_2fa !== '') params.append('requires_2fa', filters.requires_2fa);
      if (filters.success !== '') params.append('success', filters.success);
      
      const response = await fetch(`${API}/api/admin-ops/security/audit-logs?${params}`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) throw new Error('Failed to fetch audit logs');
      
      const data = await response.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [skip, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/admin-ops/security/audit-stats?days=7`, {
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setSkip(0);
  };

  const clearFilters = () => {
    setFilters({
      admin_email: '',
      action: '',
      module: '',
      date_from: '',
      date_to: '',
      requires_2fa: '',
      success: '',
    });
    setSkip(0);
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="text-amber-400" />
            Audit Log
          </h1>
          <p className="text-slate-400 mt-1">
            Security audit trail for admin actions with full UTC timestamps
          </p>
        </div>
        <Button onClick={() => { fetchLogs(); fetchStats(); }} variant="outline" size="sm">
          <RefreshCw size={14} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Activity className="text-emerald-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_actions}</p>
                <p className="text-xs text-slate-500">Actions (7 days)</p>
              </div>
            </div>
          </div>
          
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Shield className="text-amber-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.two_fa_required}</p>
                <p className="text-xs text-slate-500">2FA Required</p>
              </div>
            </div>
          </div>
          
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <ShieldCheck className="text-emerald-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.two_fa_verified}</p>
                <p className="text-xs text-slate-500">2FA Verified</p>
              </div>
            </div>
          </div>
          
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="text-red-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.failed_actions}</p>
                <p className="text-xs text-slate-500">Failed Actions</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <Filter size={16} />
            Filters
            <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          
          {Object.values(filters).some(v => v) && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
        
        {showFilters && (
          <div className="grid grid-cols-4 gap-4">
            <Input
              placeholder="Admin email..."
              value={filters.admin_email}
              onChange={(e) => handleFilterChange('admin_email', e.target.value)}
              className="bg-stone-800 border-slate-700"
            />
            
            <Select value={filters.module} onValueChange={(v) => handleFilterChange('module', v)}>
              <SelectTrigger className="bg-stone-800 border-slate-700">
                <SelectValue placeholder="Module" />
              </SelectTrigger>
              <SelectContent>
                {MODULE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.requires_2fa} onValueChange={(v) => handleFilterChange('requires_2fa', v)}>
              <SelectTrigger className="bg-stone-800 border-slate-700">
                <SelectValue placeholder="2FA Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All</SelectItem>
                <SelectItem value="true">2FA Required</SelectItem>
                <SelectItem value="false">No 2FA</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.success} onValueChange={(v) => handleFilterChange('success', v)}>
              <SelectTrigger className="bg-stone-800 border-slate-700">
                <SelectValue placeholder="Result" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All</SelectItem>
                <SelectItem value="true">Success</SelectItem>
                <SelectItem value="false">Failed</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="col-span-2 flex gap-2">
              <div className="flex-1">
                <label className="text-xs text-slate-500 mb-1 block">From Date</label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="bg-stone-800 border-slate-700"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-slate-500 mb-1 block">To Date</label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="bg-stone-800 border-slate-700"
                />
              </div>
            </div>
            
            <div className="col-span-2 flex items-end">
              <Button onClick={() => fetchLogs()} className="bg-amber-600 hover:bg-amber-700">
                <Search size={14} className="mr-2" />
                Search
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="flex items-center justify-between text-sm text-slate-400">
        <span>Showing {logs.length} of {total} audit logs</span>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - limit))}
          >
            Previous
          </Button>
          <span className="px-3 py-1 bg-stone-800 rounded text-xs">
            Page {Math.floor(skip / limit) + 1}
          </span>
          <Button 
            variant="outline" 
            size="sm" 
            disabled={skip + limit >= total}
            onClick={() => setSkip(skip + limit)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Log List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="bg-stone-900 border border-stone-800 rounded-lg p-4 animate-pulse">
              <div className="h-4 bg-stone-800 rounded w-1/3 mb-2"></div>
              <div className="h-3 bg-stone-800 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-12 text-center">
          <Shield className="mx-auto text-slate-600 mb-4" size={48} />
          <h3 className="text-lg font-medium text-white mb-2">No Audit Logs</h3>
          <p className="text-slate-400">No matching audit records found for the selected filters.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map(log => (
            <AuditLogEntry 
              key={log.id} 
              log={log} 
              onViewDetails={setSelectedLog}
            />
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <AuditDetailModal 
        log={selectedLog}
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </div>
  );
};

export default AuditLogPage;
